import React from "react";
import { FormikProps } from "formik";
import ATMLoadingButton from "src/components/UI/atoms/ATMLoadingButton/ATMLoadingButton";

type Props = {
  formikProps: FormikProps<any>;
  onClose: () => void;
  formType: "ADD" | "EDIT";
};

const FileManagerForm = ({ formikProps, onClose, formType }: Props) => {
  const { values, setFieldValue, isSubmitting } = formikProps;

  // Custom handler for multiple JPG/JPEG file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const validFiles: File[] = [];
    const invalidFiles: string[] = [];

    Array.from(files).forEach((file) => {
      const fileType = file.type.toLowerCase();
      if (fileType === "image/jpeg" || fileType === "image/jpg") {
        validFiles.push(file);
      } else {
        invalidFiles.push(file.name);
      }
    });

    if (invalidFiles.length > 0) {
      alert(
        `Only JPG and JPEG images are allowed.\nInvalid files:\n${invalidFiles.join(
          "\n"
        )}`
      );
    }

    // Append new files instead of replacing
    const existingFiles = values.files || [];
    const updatedFiles = [...existingFiles, ...validFiles];

    // Remove duplicates (same name & size)
    const uniqueFiles = updatedFiles.filter(
      (file, index, self) =>
        index ===
        self.findIndex(
          (f) => f.name === file.name && f.size === file.size
        )
    );

    setFieldValue("files", uniqueFiles);
  };

  // Remove single file
  const handleRemoveFile = (index: number) => {
    const updatedFiles = values.files.filter(
      (_: File, i: number) => i !== index
    );
    setFieldValue("files", updatedFiles);
  };

  return (
    <div className="p-4 flex flex-col gap-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="text-xl font-medium">
          {formType === "ADD" ? "Add Files" : "Update Files"}
        </div>
        <button type="button" className="hover:bg-hover" onClick={onClose}>
          Cancel
        </button>
      </div>

      {/* File Upload Section */}
      <div className="flex flex-col gap-4">
        <label className="text-sm font-medium">Upload Images (JPG/JPEG)</label>
        <input
          type="file"
          accept=".jpg,.jpeg"
          multiple
          onChange={handleFileChange}
          className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 focus:outline-none p-2"
        />

        {/* Selected Files Preview */}
        {values.files && values.files.length > 0 && (
          <div className="mt-3 border border-gray-200 rounded-lg p-2">
            <div className="text-sm font-semibold mb-1">Selected Files:</div>
            <ul className="space-y-1 text-sm text-gray-700">
              {values.files.map((file: File, index: number) => (
                <li
                  key={index}
                  className="flex justify-between items-center border-b border-gray-100 pb-1 mb-2 bg-gray-50 p-3 rounded-md"
                >
                  <span>{file.name}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveFile(index)}
                    className="text-red-500 text-xs hover:underline"
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Submit Button */}
      <div>
        <ATMLoadingButton isLoading={isSubmitting} type="submit">
          Save
        </ATMLoadingButton>
      </div>
    </div>
  );
};

export default FileManagerForm;
